import React, { Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls, TrackballControls } from '@react-three/drei';
import HashupCartridgeModel from './hashupCartridgeModel';
import { useGameColorsProvider } from '../../providers/Theme/gameColorsProvider';
import { Bloom, EffectComposer, SMAA } from '@react-three/postprocessing';
import * as THREE from 'three';
import useOscillator, { generateCubicDistribution, IOscillation } from './useOscillator';
import { useBoolean } from '@chakra-ui/react';

export enum Cartridge {
	GOLD,
	GRAY,
	CUSTOM,
	_ /** Null cartridge */,
}

const hdrMaps: { [p: number]: string } = {
	[Cartridge.GOLD]: '/assets/models/zloty/zloty.hdr',
	[Cartridge.GRAY]: '/assets/models/szary/szary.hdr',
	[Cartridge.CUSTOM]: '/assets/models/teczowy/teczowy.hdr',
	[Cartridge._]: '/assets/models/pusty/pusty.hdr',
};

export enum TextureMap {
	FALLBACK = '/assets/models/pusty/dopodmiany.jpg',
}

export interface IHashupCartridge {
	cartridge: Cartridge;
	cartridgeCoverUri?: string;
}

export const HashupCartridge = ({ cartridge, cartridgeCoverUri = TextureMap.FALLBACK }: IHashupCartridge) => {
	const isLoading = useMemo(() => cartridge === Cartridge._, [cartridge]);
	const sizeScalarDelta = 4.75;

	const [isBlobLoaded, setIsBlobLoaded] = useBoolean(false);
	const [wasUserControlled, setWasUserControlled] = useBoolean(false);

	const { callToActionColor } = useGameColorsProvider();

	const speedUpCurve = useMemo(() => generateCubicDistribution(3.75, 75, 7).slice(1, -1), []);
	const oscillator = useOscillator({
		oscillations: [
			{ period: 4000, value: 3.75 },
			...speedUpCurve.map(point => Object.create({ period: 337, value: point }) as IOscillation),
			{ period: 337, value: 75 },
		],
		shouldFire: isBlobLoaded,
	});

	const handleAnimationIdle = () => {
		if (!isBlobLoaded) {
			return;
		}

		setWasUserControlled.on();
	};

	return (
		<Canvas
			onCreated={({ gl }) => {
				gl.outputEncoding = THREE.sRGBEncoding;
			}}
			camera={{ position: [0, 0, sizeScalarDelta], fov: 30 }}
			onMouseDown={handleAnimationIdle}
			onTouchMove={handleAnimationIdle}
		>
			<Suspense
				fallback={
					<mesh>
						<boxBufferGeometry
							attach="geometry"
							args={[1, 1, 1]}
						/>
						<ambientLight intensity={0.1} />
						<meshStandardMaterial
							attach="material"
							transparent
							opacity={0.5}
							color={callToActionColor}
						/>
					</mesh>
				}
			>
				<HashupCartridgeModel
					cartridge={cartridge}
					coverTextureUri={cartridgeCoverUri}
					setIsBlobLoaded={setIsBlobLoaded}
				/>

				<Environment files={hdrMaps[cartridge]} />
				{isLoading && <ambientLight />}

				<OrbitControls
					makeDefault
					autoRotate
					autoRotateSpeed={!wasUserControlled ? oscillator.value : 1.337}
					enablePan={false}
					enableZoom={false}
				/>
				<TrackballControls
					noRotate={true}
					noPan={true}
					noZoom={true}
					zoomSpeed={1.5}
				/>

				<EffectComposer multisampling={8}>
					<SMAA />
					<Bloom
						kernelSize={2}
						luminanceThreshold={0.75}
						luminanceSmoothing={1}
						intensity={0.66}
					/>
					<Bloom
						kernelSize={1}
						luminanceThreshold={0.42}
						luminanceSmoothing={1}
						intensity={0.16}
					/>
				</EffectComposer>
			</Suspense>
		</Canvas>
	);
};
